/**
 * GlobalView — Satellite Tracker Service
 *
 * Camera tracking for selected satellites, mirroring the aircraft tracker.
 * Creates a hidden Cesium Entity with a CallbackProperty that reads
 * the satellite's current billboard position from SpaceLayerRenderer.
 * Sets viewer.trackedEntity for orbit-lock camera following.
 */

import * as Cesium from 'cesium';
import { useGlobeStore } from '../stores/globeStore';
import { useEntityStore } from '../stores/entityStore';
import { getSatellitePosition } from './layers/SpaceLayerRenderer';

// ─── State ───────────────────────────────────────────────────────

let trackerEntity: Cesium.Entity | null = null;
let trackedSatId: string | null = null;

// ─── Public API ──────────────────────────────────────────────────

/**
 * Start tracking a satellite.
 * Creates a hidden Cesium Entity and sets it as viewer.trackedEntity.
 */
export function startSatelliteTracking(entityId: string): void {
  const entity = useEntityStore.getState().entities.get(entityId);
  if (!entity || entity.type !== 'satellite') return;

  const viewer = useGlobeStore.getState().viewer;
  if (!viewer || viewer.isDestroyed()) return;

  // Save current camera orientation
  const camera = viewer.camera;
  const savedHeading = camera.heading;
  const savedPitch = camera.pitch;

  // Compute default cartesian from entity store position
  const defaultCartesian = Cesium.Cartesian3.fromDegrees(
    entity.position.lon,
    entity.position.lat,
    entity.position.alt,
  );

  // Current distance from camera to satellite
  const currentPos = getSatellitePosition(entityId) ?? defaultCartesian;
  const savedRange = Cesium.Cartesian3.distance(camera.positionWC, currentPos);

  // Clean up previous tracking
  stopSatelliteTracking();

  // Update store state
  useGlobeStore.getState().setTrackedEntity(entityId);
  useGlobeStore.getState().setSelectedEntity(entityId);

  trackedSatId = entityId;

  // Create hidden entity with callback position
  trackerEntity = viewer.entities.add({
    position: new Cesium.CallbackProperty(() => {
      if (!trackedSatId) return defaultCartesian;
      return getSatellitePosition(trackedSatId) ?? defaultCartesian;
    }, false) as unknown as Cesium.PositionProperty,
    point: { pixelSize: 1, color: Cesium.Color.TRANSPARENT },
  });

  // Lock camera to satellite
  viewer.trackedEntity = trackerEntity;

  // Restore camera orientation
  requestAnimationFrame(() => {
    if (!viewer.isDestroyed() && viewer.trackedEntity === trackerEntity) {
      const offset = new Cesium.HeadingPitchRange(
        savedHeading,
        savedPitch,
        savedRange,
      );
      viewer.scene.camera.lookAt(currentPos, offset);
    }
  });

  viewer.scene.requestRender();
  console.log(`[SatTracker] Tracking satellite: ${entityId}`);
}

/**
 * Stop satellite tracking — release camera.
 */
export function stopSatelliteTracking(): void {
  const viewer = useGlobeStore.getState().viewer;

  // Only clear tracked if it's a satellite we're tracking
  if (trackedSatId && useGlobeStore.getState().trackedEntityId === trackedSatId) {
    useGlobeStore.getState().setTrackedEntity(null);
  }
  trackedSatId = null;

  if (viewer && !viewer.isDestroyed()) {
    if (viewer.trackedEntity === trackerEntity) {
      viewer.trackedEntity = undefined;
    }

    if (trackerEntity) {
      viewer.entities.remove(trackerEntity);
    }

    viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
  }

  trackerEntity = null;
  console.log('[SatTracker] Tracking stopped');
}

/**
 * Check if currently tracking a satellite.
 */
export function isSatelliteTracking(): boolean {
  return trackedSatId !== null;
}
